// All OpenWeatherMap calls go through /api/* serverless functions.
// The API key never reaches the browser.

const GEO_PROXY = '/api/geo';
const GEOREV_PROXY = '/api/georev';
const WEATHER_PROXY = '/api/weather';

export async function fetchCitySuggestionsAPI(query) {
  if (!query || query.trim().length < 2) return [];

  const res = await fetch(`${GEO_PROXY}?q=${encodeURIComponent(query)}`);

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch city suggestions');
  }

  const data = await res.json();

  return data.map(item => ({
    name: item.name,
    state: item.state || '',
    country: item.country,
    lat: item.lat,
    lon: item.lon,
  }));
}

export async function fetchLocationByCoords(lat, lon) {
  const res = await fetch(`${GEOREV_PROXY}?lat=${lat}&lon=${lon}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to get location from coordinates');
  }
  return res.json();
}

export async function fetchWeatherDataAPI(locationOrCoords) {
  if (!locationOrCoords) throw new Error('Location is required');

  let lat, lon;

  if (typeof locationOrCoords === 'object' && locationOrCoords.lat != null) {
    ({ lat, lon } = locationOrCoords);
  } else {
    const suggestions = await fetchCitySuggestionsAPI(locationOrCoords);
    if (!suggestions.length) throw new Error('City not found');
    ({ lat, lon } = suggestions[0]);
  }

  const res = await fetch(
    `${WEATHER_PROXY}?lat=${lat}&lon=${lon}`
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch weather data');
  }

  const { weather: w, forecast: f } = await res.json();

  // Build forecast array — one entry per day, slot closest to noon local time
  const forecastDays = [];
  const dayMap = new Map();

  for (const item of f.list) {
    const date = new Date((item.dt + w.timezone) * 1000);
    const dayKey = date.toISOString().slice(0, 10);
    const hour = date.getUTCHours();

    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, item);
    } else {
      const existing = dayMap.get(dayKey);
      const existingHour = new Date((existing.dt + w.timezone) * 1000).getUTCHours();
      if (Math.abs(hour - 12) < Math.abs(existingHour - 12)) {
        dayMap.set(dayKey, item);
      }
    }
  }

  for (const [, item] of dayMap) {
    if (forecastDays.length >= 7) break;
    const date = new Date((item.dt + w.timezone) * 1000);
    forecastDays.push({
      day: date.toLocaleDateString('en-GB', { weekday: 'short' }),
      temp: Math.round(item.main.temp),
      condition: item.weather[0].main,
      rainProbability: Math.round((item.pop || 0) * 100),
      humidity: item.main.humidity,
      windSpeed: item.wind.speed,
      windDeg: item.wind.deg,
    });
  }

  const isDaytime = w.dt > w.sys.sunrise && w.dt < w.sys.sunset;

  return {
    temperature: w.main.temp,
    humidity: w.main.humidity,
    windSpeed: w.wind.speed,
    windDeg: w.wind.deg,
    condition: w.weather[0].main,
    description: w.weather[0].description,
    isDaytime,
    rainProbability: f.list[0] ? Math.round((f.list[0].pop || 0) * 100) : 0,
    cityName: w.name,
    country: w.sys.country,
    timezone: w.timezone,
    forecast: forecastDays,
  };
}
